import { Component, OnDestroy, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import {
    combineLatest,
    Observable,
    of,
    ReplaySubject,
    Subject,
    BehaviorSubject
} from 'rxjs';
import {
    catchError,
    filter,
    map,
    switchMap,
    take,
    takeWhile,
    tap
} from 'rxjs/operators';
import * as fromDataServices from '../../data-services';
import * as fromShared from '../../shared';
import * as fromUser from '../../user-info';
import * as fromSettings from '../../company-setting/store';
import * as fromQBO from '../../quickbooks/store';
import { QuickbooksService } from '../../quickbooks/quickbooks.services';
import { handleServiceError } from '../../shared/errors-sidenav';
@Component({
    selector: 'ef-recalculate-transactions-feature',
    templateUrl: './recalculate-transactions-feature.component.html',
    styleUrls: ['./recalculate-transactions-feature.component.scss'],
    standalone: false
})
export class RecalculateTransactionsFeatureComponent
    implements OnInit, OnDestroy
{
    process$: Observable<fromShared.Process>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromShared.IStatusMessage>;
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);

    recalculateContractsDataService: EntityCollectionService<fromDataServices.RecalculateContracts.RecalculateContract>;
    recalculateSchedulesDataService: EntityCollectionService<fromDataServices.RecalculateSchedules.RecalculateSchedule>;
    recalculateSettingsDataService: EntityCollectionService<fromDataServices.RecalculateSettings.RecalculateSetting>;

    public schedules$: Observable<
        fromDataServices.RecalculateSchedules.RecalculateSchedule[]
    >;
    public schedules: fromDataServices.RecalculateSchedules.RecalculateSchedule[];
    public contracts$: Observable<
        fromDataServices.RecalculateContracts.RecalculateContract[]
    >;
    public initialContracts$: Observable<
        fromDataServices.RecalculateContracts.RecalculateContract[]
    >;
    public isQBOEnabled$: Observable<boolean>;
    public hasValidTokens$: Observable<boolean>;
    public settings$: Observable<
        fromDataServices.RecalculateSettings.RecalculateSetting[]
    >;
    public userInfo$: Observable<fromUser.IUserInfo>;

    public clearSelectedSchedules$: ReplaySubject<boolean> =
        new ReplaySubject<boolean>(1);

    FYStart: any;
    FYEnd: any;

    active = true;

    scheduleTimer: any;
    showTimer = false;
    step1Timer$: Observable<boolean>;

    CompanySettingsFYStartDate: any;
    CompanySettingsFYEndDate: any;

    processingText = 'Processing...';

    processScheduleIDsString: string;

    private contractsRequest$ = new Subject<{
        PeriodStartDate?: Date;
        PeriodEndDate?: Date;
    }>();

    private contractsLoadingSubject = new BehaviorSubject<boolean>(false);
    public contractsLoading$ = this.contractsLoadingSubject.pipe(
        takeWhile(() => this.active)
    );

    constructor(
        public entityServices: EntityServices,
        private log: fromShared.Services.Logging.LoggingService,
        private qbService: QuickbooksService,
        private store: Store
    ) {
        log.verbose({ type: 'RecalculateTransactionsFeatureComponent::' });
        this.recalculateContractsDataService =
            entityServices.getEntityCollectionService<fromDataServices.RecalculateContracts.RecalculateContract>(
                fromDataServices.RecalculateContracts.entityName
            );
        this.recalculateSchedulesDataService =
            entityServices.getEntityCollectionService<fromDataServices.RecalculateSchedules.RecalculateSchedule>(
                fromDataServices.RecalculateSchedules.entityName
            );
        this.recalculateSettingsDataService =
            entityServices.getEntityCollectionService<fromDataServices.RecalculateSettings.RecalculateSetting>(
                fromDataServices.RecalculateSettings.entityName
            );
    }

    ngOnInit(): void {
        this.setupObservables();
        this.setupSubscriptions();
        this.recalculateSettingsDataService.getWithQuery({ CatID: '8' });
        this.recalculateSettingsDataService.getWithQuery({
            SettingType: 'FYStartDate'
        });
        this.recalculateSettingsDataService.getWithQuery({
            SettingType: 'FYEndDate'
        });
        this.recalculateSchedulesDataService.getWithQuery({
            FyStartDate: this.CompanySettingsFYStartDate,
            FYEndDate: this.CompanySettingsFYEndDate
        });
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'QuickbooksOnline'
            })
        );
    }

    ngOnDestroy() {
        this.active = false;
        this.recalculateSchedulesDataService.clearCache();
        this.recalculateContractsDataService.clearCache();
        this.recalculateSettingsDataService.clearCache();
        if (this.scheduleTimer) {
            clearInterval(this.scheduleTimer);
            this.scheduleTimer = null;
            this.showTimer = false;
        }
    }

    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.isQBOEnabled$ = this.store.pipe(select(fromSettings.isQBOEnabled));
        this.hasValidTokens$ = this.store.pipe(
            select(fromQBO.getHasValidTokens)
        );
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
        this.schedules$ = this.recalculateSchedulesDataService.entities$.pipe(
            map(x => clone(x))
        );

        this.contracts$ = this.contractsRequest$.pipe(
            tap(request => {
                this.log.verbose({
                    type: 'RecalculateTransactionsFeatureComponent:contracts$',
                    request
                });
            }),
            tap(() => this.contractsLoadingSubject.next(true)),
            map(request => request as any),
            switchMap(request => {
                this.recalculateContractsDataService.clearCache();
                return this.recalculateContractsDataService
                    .getWithQuery(request)
                    .pipe(
                        map(x => clone(x)),
                        catchError(err => {
                            this.log.error({
                                type: 'RecalculateTransactionsFeatureComponent:contracts$',
                                error: err
                            });
                            return of([]);
                        }),
                        tap(() => {
                            // Set loading to false when the request completes
                            this.contractsLoadingSubject.next(false);
                        })
                    );
            }),
            takeWhile(() => this.active)
        );

        this.initialContracts$ =
            this.recalculateContractsDataService.entities$.pipe(
                map(x => clone(x))
            );
        this.settings$ = this.recalculateSettingsDataService.entities$.pipe(
            map(x => clone(x))
        );
    }

    setupSubscriptions() {
        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });

        this.userInfo$
            .pipe(takeWhile(() => this.active))
            .subscribe(userInfo => {
                this.FYStart = userInfo.FYStartDate;
                this.FYEnd = userInfo.FYEndDate;
            });
        this.schedules$
            .pipe(
                takeWhile(() => this.active),
                filter(x => !!x && x.length > 0)
            )
            .subscribe(schedules => {
                // this.schedules = [];
                this.schedules = schedules;
                this.log.verbose({
                    type: 'RecalculateTransactionsFeatureComponent:setupSubscriptions:schedules',
                    schedules,
                    scheduleTimer: this.scheduleTimer
                });

                this.processingText =
                    schedules.find(x => x.Userlogdetail != null)
                        ?.Userlogdetail ?? 'Processing...';

                if (schedules.some(schedule => schedule.Timer)) {
                    if (this.scheduleTimer == null) {
                        this.log.verbose({
                            type: 'RecalculateTransactionsFeatureComponent:setupSubscriptions:schedules:Timer=true',
                            message: 'some timer is true',
                            schedules: this.schedules.filter(
                                x => x.Timer == true
                            )
                        });
                        this.scheduleTimer = setInterval(() => {
                            this.log.verbose({
                                type: 'RecalculateTransactionsFeatureComponent:setupSubscriptions:scheduleTimer',
                                message: 'some timer is true',
                                schedules: this.schedules.filter(
                                    x => x.Timer == true
                                )
                            });
                            this.recalculateSchedulesDataService.getWithQuery({
                                FYStartDate: this.CompanySettingsFYStartDate,
                                FYEndDate: this.CompanySettingsFYEndDate
                            });
                        }, 1000);
                    }
                } else {
                    if (this.scheduleTimer != null) {
                        this.clearSelectedSchedules$.next(true);
                        clearInterval(this.scheduleTimer);
                        this.scheduleTimer = null;
                    }
                }

                if (this.scheduleTimer != null && this.showTimer == false) {
                    this.clearSelectedSchedules$.next(true);
                    clearInterval(this.scheduleTimer);
                    this.scheduleTimer = null;
                    this.recalculateSettingsDataService.getWithQuery({
                        CatID: '8'
                    });
                    this.clearSelectedSchedules$.next(true);
                    setTimeout(() => {
                        this.recalculateSchedulesDataService.getWithQuery({
                            FYStartDate: this.CompanySettingsFYStartDate,
                            FYEndDate: this.CompanySettingsFYEndDate
                        });
                    }, 30000);
                }

                this.showTimer = schedules.some(x => x.Timer);
            });
        // this.showTimer = this.schedules$.pipe(
        //     filter(x => x.some(y => y.Timer))
        // );

        this.settings$
            .pipe(
                takeWhile(() => this.active),
                filter(x => !!x && x.length > 0)
            )
            .subscribe(settings => {
                const fyStartDate = settings.find(
                    x => x.SettingType == 'FYStartDate'
                );
                this.CompanySettingsFYStartDate =
                    fyStartDate && fyStartDate.SettingValue;

                const fyEndDate = settings.find(
                    x => x.SettingType == 'FYEndDate'
                );
                this.CompanySettingsFYEndDate =
                    fyEndDate && fyEndDate.SettingValue;
            });
    }
    onStep1($event: { schedules: string; glOnly: boolean }) {
        this.log.verbose({
            type: 'RecalculateTransactionsFeatureComponent:onStep1',
            schedules: this.schedules
        });
        clearInterval(this.scheduleTimer);
        this.scheduleTimer = null;
        this.showTimer = true;
        this.schedules[0] = { ...this.schedules[0], Timer: true };
        this.qbService
            .SyncTransactions($event.schedules, $event.glOnly)
            .then(() => {
                this.scheduleTimer = setInterval(() => {
                    this.recalculateSchedulesDataService.getWithQuery({
                        FYStartDate: this.CompanySettingsFYStartDate,
                        FYEndDate: this.CompanySettingsFYEndDate
                    });
                }, 1000);
            })
            .catch(err => {
                clearInterval(this.scheduleTimer);
                handleServiceError(err, of(null))
                    .pipe(take(1))
                    .subscribe(a => this.store.dispatch(a));
                this.showTimer = false;
            });
    }

    onStep2($event) {
        this.log.verbose({
            type: 'RecalculateTransactionsFeatureComponent:onStep2',
            schedules: this.schedules
        });
        clearInterval(this.scheduleTimer);
        this.scheduleTimer = null;
        this.recalculateSchedulesDataService.getWithQuery({
            ProcessStep: '2',
            ProcessScheduleID_List: $event
        });
        this.showTimer = true;
        // start timer
        this.schedules[0] = { ...this.schedules[0], Timer: true };
        // this.recalculateSchedulesDataService.clearCache();
        this.scheduleTimer = setInterval(() => {
            this.recalculateSchedulesDataService
                .getWithQuery({
                    FYStartDate: this.CompanySettingsFYStartDate,
                    FYEndDate: this.CompanySettingsFYEndDate
                })
                .pipe(
                    take(1),
                    catchError(err => {
                        clearInterval(this.scheduleTimer);
                        return handleServiceError(err, of(null));
                    })
                );
        }, 2000);
    }

    onStep3($event) {
        this.log.verbose({
            type: 'RecalculateTransactionsFeatureComponent:onStep3',
            schedules: this.schedules
        });
        clearInterval(this.scheduleTimer);
        this.scheduleTimer = null;
        this.recalculateSchedulesDataService.getWithQuery({
            ProcessStep: $event
        });
        this.showTimer = true;
        // start timer
        this.schedules[0] = { ...this.schedules[0], Timer: true };
        this.scheduleTimer = setInterval(() => {
            this.recalculateSchedulesDataService
                .getWithQuery({
                    FYStartDate: this.CompanySettingsFYStartDate,
                    FYEndDate: this.CompanySettingsFYEndDate
                })
                .pipe(
                    take(1),
                    catchError(err => {
                        clearInterval(this.scheduleTimer);
                        return handleServiceError(err, of(null));
                    })
                );
        }, 1000);
    }

    onGetSchedules($event) {
        this.log.verbose({
            type: 'RecalculateTransactionsFeatureComponent:onGetSchedules',
            $event
        });

        // Trigger the schedules query
        this.recalculateSchedulesDataService.clearCache();
        this.recalculateSchedulesDataService.getWithQuery({
            FYStartDate: this.CompanySettingsFYStartDate,
            FYEndDate: this.CompanySettingsFYEndDate
        });
    }

    onGetContracts($event: { PeriodStartDate?: Date; PeriodEndDate?: Date }) {
        this.log.verbose({
            type: 'RecalculateTransactionsFeatureComponent:onGetContracts',
            $event
        });

        // Emit a new request for contracts
        this.contractsRequest$.next($event);
    }

    onGetSettings($event) {
        this.recalculateSettingsDataService.clearCache();
        this.recalculateSettingsDataService.getWithQuery({ CatID: '8' });
    }

    onApplyInvoiceChanges($event) {
        this.recalculateContractsDataService.upsert($event);
    }

    onUpdateSettings($event) {
        this.recalculateSettingsDataService.upsert($event);
    }
}
